import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MailWarning, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_HIDE_KEY = "trademind:verify-banner-hidden-at";

/**
 * Sanfte Erinnerung an unbestätigte Nutzer:innen, ihre E-Mail zu verifizieren.
 * Login wird NICHT blockiert. Dismissible (24 h), nach Verifikation weg.
 */
export default function VerifyBanner() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const t = Number(localStorage.getItem(LS_HIDE_KEY) || 0);
      return t && Date.now() - t < 24 * 60 * 60 * 1000;
    } catch { return false; }
  });

  if (!user || user.email_verified || dismissed) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/resend-verify`, { email: user.email });
      toast.success("Bestätigungs-E-Mail wurde erneut versendet", {
        description: "Bitte prüfe auch deinen Spam-Ordner.",
      });
    } catch {
      toast.error("Erneutes Senden fehlgeschlagen — bitte in einer Minute erneut versuchen.");
    } finally {
      setBusy(false);
    }
  };

  const hide = () => {
    try { localStorage.setItem(LS_HIDE_KEY, String(Date.now())); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-xl border border-[hsl(32_60%_55%/0.45)] bg-[hsl(32_60%_55%/0.10)] px-4 py-3 flex items-start gap-3"
      data-testid="verify-email-banner"
    >
      <MailWarning className="h-5 w-5 text-[hsl(32_60%_72%)] shrink-0 mt-0.5" strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground">
          <span className="font-medium">Bitte bestätige deine E-Mail-Adresse.</span>{" "}
          <span className="text-muted-foreground">
            Wir haben dir einen Link an <span className="text-foreground">{user.email}</span> geschickt. So können wir dir bei Passwort-Reset & Support sicher zurückschreiben.
          </span>
        </p>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={resend}
            disabled={busy}
            className="text-sm text-foreground underline underline-offset-4 hover:opacity-80 disabled:opacity-50"
            data-testid="verify-email-resend"
          >
            {busy ? "Sende…" : "Bestätigungs-Mail erneut senden"}
          </button>
          <button
            type="button"
            onClick={hide}
            className="text-xs text-muted-foreground hover:text-foreground"
            data-testid="verify-email-dismiss"
          >
            Später
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={hide}
        className="text-muted-foreground hover:text-foreground shrink-0"
        aria-label="Banner schließen"
        data-testid="verify-email-close"
      >
        <X className="h-4 w-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}
